import React, { useState } from 'react';
import { Loader2, AlertCircle, X, Repeat, Calendar, CalendarRange } from 'lucide-react';
import { crearBloqueo, editarBloqueo } from '../api';
import { TimePicker24h } from '../../ui-v2';

const DOW = [
  { v: 1, l: 'Lunes' },
  { v: 2, l: 'Martes' },
  { v: 3, l: 'Miércoles' },
  { v: 4, l: 'Jueves' },
  { v: 5, l: 'Viernes' },
  { v: 6, l: 'Sábado' },
  { v: 0, l: 'Domingo' },
];

const PATRONES = [
  { v: 'semanal', l: 'Recurrente semanal', icon: Repeat, hint: 'Mismo día de la semana, todas las semanas' },
  { v: 'dia',     l: 'Día concreto',       icon: Calendar, hint: 'Una fecha específica' },
  { v: 'rango',   l: 'Rango de fechas',    icon: CalendarRange, hint: 'Varios días seguidos (vacaciones, formación…)' },
];

/**
 * EditorBloqueo — modal de alta/edición de bloqueo personal del técnico.
 *
 * Props:
 *  - bloqueo: objeto existente o null (modo creación)
 *  - onClose, onOk
 */
export default function EditorBloqueo({ bloqueo, onClose, onOk }) {
  const modoEdicion = !!bloqueo;
  const [patron, setPatron] = useState(bloqueo?.patron || 'semanal');
  const [diaSemana, setDiaSemana] = useState(
    bloqueo?.dia_semana !== null && bloqueo?.dia_semana !== undefined
      ? bloqueo.dia_semana : 1
  );
  const [fechaInicio, setFechaInicio] = useState(bloqueo?.fecha_inicio || '');
  const [fechaFin, setFechaFin] = useState(bloqueo?.fecha_fin || '');
  const [diaEntero, setDiaEntero] = useState(!bloqueo?.hora_inicio);
  const [horaInicio, setHoraInicio] = useState(bloqueo?.hora_inicio || '');
  const [horaFin, setHoraFin] = useState(bloqueo?.hora_fin || '');
  const [motivo, setMotivo] = useState(bloqueo?.motivo || '');

  const [guardando, setGuardando] = useState(false);
  const [errores, setErrores] = useState([]);
  const [errorGeneral, setErrorGeneral] = useState(null);

  function errorDe(campo) {
    return errores.find((e) => e.campo === campo)?.mensaje;
  }

  async function guardar() {
    setGuardando(true);
    setErrores([]);
    setErrorGeneral(null);

    const payload = {
      patron,
      motivo: motivo.trim() || null,
    };
    if (patron === 'semanal') payload.dia_semana = Number(diaSemana);
    if (patron === 'dia') payload.fecha_inicio = fechaInicio || null;
    if (patron === 'rango') {
      payload.fecha_inicio = fechaInicio || null;
      payload.fecha_fin = fechaFin || null;
    }
    if (!diaEntero) {
      payload.hora_inicio = horaInicio || null;
      payload.hora_fin = horaFin || null;
    }

    try {
      if (modoEdicion) await editarBloqueo(bloqueo.id, payload);
      else await crearBloqueo(payload);
      if (onOk) await onOk();
    } catch (e) {
      console.error('[guardarBloqueo]', e);
      const errs = e?.response?.data?.errores;
      if (Array.isArray(errs)) setErrores(errs);
      else setErrorGeneral(e?.response?.data?.error || 'No se pudo guardar el bloqueo.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 z-50 grid place-items-center px-4 py-8 overflow-y-auto"
      onClick={guardando ? undefined : onClose}
    >
      <div
        className="bg-white rounded-lg shadow-lg max-w-xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-900">
            {modoEdicion ? 'Editar bloqueo' : 'Nuevo bloqueo'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={guardando}
            className="w-7 h-7 grid place-items-center rounded-md hover:bg-slate-100 disabled:opacity-50"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Patrón */}
          <div>
            <p className="text-xs font-medium text-slate-700 mb-2">Tipo de bloqueo</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {PATRONES.map((p) => {
                const Icon = p.icon;
                const activo = patron === p.v;
                return (
                  <button
                    key={p.v}
                    type="button"
                    onClick={() => setPatron(p.v)}
                    className={
                      'border rounded-lg p-3 text-left transition-colors ' +
                      (activo
                        ? 'border-slate-900 bg-slate-900 text-white'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400')
                    }
                  >
                    <Icon className="w-4 h-4 mb-1" />
                    <div className="text-sm font-medium">{p.l}</div>
                    <div className={'text-[10px] mt-0.5 ' + (activo ? 'text-slate-300' : 'text-slate-500')}>
                      {p.hint}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Campos según patrón */}
          {patron === 'semanal' && (
            <Campo label="Día de la semana" error={errorDe('dia_semana')}>
              <select
                value={diaSemana}
                onChange={(e) => setDiaSemana(e.target.value)}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white w-48"
              >
                {DOW.map((d) => (
                  <option key={d.v} value={d.v}>{d.l}</option>
                ))}
              </select>
            </Campo>
          )}

          {patron === 'dia' && (
            <Campo label="Fecha" error={errorDe('fecha_inicio')}>
              <input
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white w-48"
              />
            </Campo>
          )}

          {patron === 'rango' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Campo label="Desde" error={errorDe('fecha_inicio')}>
                <input
                  type="date"
                  value={fechaInicio}
                  onChange={(e) => setFechaInicio(e.target.value)}
                  className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white w-full"
                />
              </Campo>
              <Campo label="Hasta" error={errorDe('fecha_fin')}>
                <input
                  type="date"
                  value={fechaFin}
                  onChange={(e) => setFechaFin(e.target.value)}
                  className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white w-full"
                />
              </Campo>
            </div>
          )}

          {/* Día entero o franja */}
          <div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={diaEntero}
                onChange={(e) => setDiaEntero(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300"
              />
              Día entero (sin franja horaria específica)
            </label>
            {!diaEntero && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                <Campo label="Hora inicio" error={errorDe('hora_inicio')}>
                  <TimePicker24h
                    value={horaInicio}
                    onChange={setHoraInicio}
                    ariaLabel="Hora inicio del bloqueo"
                  />
                </Campo>
                <Campo label="Hora fin" error={errorDe('hora_fin')}>
                  <TimePicker24h
                    value={horaFin}
                    onChange={setHoraFin}
                    ariaLabel="Hora fin del bloqueo"
                  />
                </Campo>
              </div>
            )}
          </div>

          {/* Motivo */}
          <Campo label="Motivo (opcional)" hint="Vacaciones, formación, médico, etc.">
            <input
              type="text"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              maxLength={255}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
            />
          </Campo>

          {errorGeneral && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-md p-2.5 flex items-start gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              {errorGeneral}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-100 bg-slate-50 rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            disabled={guardando}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={guardar}
            disabled={guardando}
            className="px-3 py-1.5 text-sm rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60 flex items-center gap-1.5"
          >
            {guardando && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {modoEdicion ? 'Guardar cambios' : 'Crear bloqueo'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Campo({ label, hint, error, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
      {children}
      {hint && !error && <p className="text-[10px] text-slate-400 mt-1">{hint}</p>}
      {error && <p className="text-[10px] text-red-600 mt-1">{error}</p>}
    </div>
  );
}
